import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../services/supabase';
import { SolicitacoesService } from '../services/solicitacoesService';
import DadosLogisticos from './DadosLogisticos';

interface Agendamento {
  id: string;
  codigo: string;
  fornecedor: string;
  mercadorias: string;
  status: string;
  ocorrencia: string | null;
  ordem_chegada: number | null;
  hora_ordem_chegada: string | null;
  data_programada: string;
  created_at: string;
}

interface AgendamentosPorDia {
  data: string;
  quantidade: number;
  agendamentos: Agendamento[];
}

const AreaRCA: React.FC = () => {
  const { filialAtual } = useApp();
  const [abaAtiva, setAbaAtiva] = useState<'agendamentos' | 'dados_logisticos'>('agendamentos');
  const [loading, setLoading] = useState(true);
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [agendamentosPorDia, setAgendamentosPorDia] = useState<AgendamentosPorDia[]>([]);
  const [buscaFornecedor, setBuscaFornecedor] = useState('');
  const [agendamentosFornecedor, setAgendamentosFornecedor] = useState<Agendamento[]>([]);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    carregarAgendamentos();
  }, [filialAtual]);

  useEffect(() => {
    // Definir datas padrão (hoje até 30 dias)
    const hoje = new Date();
    const daqui30dias = new Date();
    daqui30dias.setDate(hoje.getDate() + 30);
    
    setDataInicio(hoje.toISOString().split('T')[0]);
    setDataFim(daqui30dias.toISOString().split('T')[0]);
  }, []);

  const carregarAgendamentos = async () => {
    try {
      setLoading(true);
      
      // Buscar todas as solicitações
      let query = supabase
        .from('solicitacoes_agendamento')
        .select('*')
        .limit(100);

      if (filialAtual) {
        query = query.eq('filial', filialAtual);
      }

      const { data: solicitacoes, error } = await query;

      if (error) throw error;

      // Mapear para o formato esperado
      const agendamentosMapeados: Agendamento[] = (solicitacoes || []).map(solicitacao => ({
        id: solicitacao.id,
        codigo: solicitacao.codigo || `SOL-${solicitacao.id.slice(-6)}`,
        fornecedor: solicitacao.fornecedor || 'Não informado',
        mercadorias: solicitacao.fornecedor || 'Não informado',
        status: solicitacao.status,
        ocorrencia: null,
        ordem_chegada: solicitacao.ordem_chegada,
        hora_ordem_chegada: solicitacao.hora_ordem_chegada,
        data_programada: solicitacao.data || solicitacao.created_at,
        created_at: solicitacao.created_at
      }));

      // Filtrar tabela principal para mostrar agendamentos ativos no período selecionado
      const statusAtivos = ['Chegou', 'Descarregando', 'Em Rota', 'APROVADO'];
      
      const agendamentosFiltrados = agendamentosMapeados.filter(agendamento => {
        const dataAgendamento = new Date(agendamento.data_programada).toISOString().split('T')[0];
        const isStatusAtivo = statusAtivos.includes(agendamento.status);
        const isNoPeriodo = dataAgendamento >= dataInicio && dataAgendamento <= dataFim;
        return isStatusAtivo && isNoPeriodo;
      });

      setAgendamentos(agendamentosFiltrados);
      
      // Filtrar próximos agendamentos para mostrar apenas amanhã em diante
      const amanha = new Date();
      amanha.setDate(amanha.getDate() + 1);
      const dataAmanha = amanha.toISOString().split('T')[0];
      
      const agendamentosFuturos = agendamentosMapeados.filter(agendamento => {
        const dataAgendamento = new Date(agendamento.data_programada).toISOString().split('T')[0];
        return dataAgendamento >= dataAmanha;
      });

      // Agrupar por dia
      const agrupadosPorDia = agruparPorDia(agendamentosFuturos);
      setAgendamentosPorDia(agrupadosPorDia);
      
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const agruparPorDia = (agendamentos: Agendamento[]): AgendamentosPorDia[] => {
    const grupos = new Map<string, Agendamento[]>();
    
    agendamentos.forEach(agendamento => {
      const data = new Date(agendamento.data_programada).toLocaleDateString('pt-BR');
      if (!grupos.has(data)) {
        grupos.set(data, []);
      }
      grupos.get(data)!.push(agendamento);
    });
    
    return Array.from(grupos.entries()).map(([data, agendamentos]) => ({
      data,
      quantidade: agendamentos.length,
      agendamentos
    }));
  };

  const buscarAgendamentosFornecedor = async () => {
    if (!buscaFornecedor.trim()) {
      setAgendamentosFornecedor([]);
      return;
    }

    try {
      // Buscar agendamentos do fornecedor - sem filtro de status
      const { data, error } = await supabase
        .from('solicitacoes_agendamento')
        .select('*')
        .ilike('fornecedor', `%${buscaFornecedor}%`)
        .limit(20);

      if (error) throw error;

      const agendamentosMapeados: Agendamento[] = (data || []).map(solicitacao => ({
        id: solicitacao.id,
        codigo: solicitacao.codigo || `SOL-${solicitacao.id.slice(-6)}`,
        fornecedor: solicitacao.fornecedor || 'Não informado',
        mercadorias: solicitacao.fornecedor || 'Não informado',
        status: solicitacao.status,
        ocorrencia: null,
        ordem_chegada: solicitacao.ordem_chegada,
        hora_ordem_chegada: solicitacao.hora_ordem_chegada,
        data_programada: solicitacao.data || solicitacao.created_at,
        created_at: solicitacao.created_at
      }));

      setAgendamentosFornecedor(agendamentosMapeados);
    } catch (error) {
      console.error('Erro ao buscar agendamentos do fornecedor:', error);
    }
  };

  const formatarData = (dataString: string) => {
    // Converter string para Date usando fuso horário local para evitar problemas de UTC
    if (dataString.includes('T')) {
      const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } else {
      const [ano, mes, dia] = dataString.split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APROVADO': return 'bg-green-100 text-green-800';
      case 'Em Rota': return 'bg-blue-100 text-blue-800';
      case 'Entregue': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOcorrenciaDisplay = (ocorrencia: string | null) => {
    if (!ocorrencia) return 'Nenhuma';
    
    try {
      const parseado = JSON.parse(ocorrencia);
      if (Array.isArray(parseado)) {
        return parseado[0]?.motivo || 'Ocorrência';
      } else if (parseado.motivos) {
        return parseado.motivos[0] || 'Ocorrência';
      } else {
        return parseado.motivo || 'Ocorrência';
      }
    } catch {
      const partes = ocorrencia.split(' | Obs: ');
      return partes[0].trim() || 'Ocorrência';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Área do RCA</h1>
        
        {/* Abas */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setAbaAtiva('agendamentos')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === 'agendamentos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Agendamentos
            </button>
            <button
              onClick={() => setAbaAtiva('dados_logisticos')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === 'dados_logisticos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dados Logísticos
            </button>
          </nav>
        </div>

        {abaAtiva === 'agendamentos' ? (
          <>
        {/* Filtros de período */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={carregarAgendamentos}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Filtrar
            </button>
          </div>
        </div>

        {/* Busca por fornecedor */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Buscar Agendamentos por Fornecedor</h2>
          <div className="flex gap-4">
            <input
              type="text"
              value={buscaFornecedor}
              onChange={(e) => setBuscaFornecedor(e.target.value)}
              placeholder="Digite o nome do fornecedor..."
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <button
              onClick={buscarAgendamentosFornecedor}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Buscar
            </button>
          </div>
          
          {agendamentosFornecedor.length > 0 && (
            <div className="mt-4">
              <h3 className="text-md font-medium text-gray-700 mb-2">
                Próximos agendamentos de "{buscaFornecedor}":
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {agendamentosFornecedor.map((agendamento) => (
                  <div key={agendamento.id} className="bg-white p-3 rounded border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{agendamento.codigo}</p>
                        <p className="text-sm text-gray-600">{formatarData(agendamento.data_programada)}</p>
                        <p className="text-sm text-gray-500">{agendamento.mercadorias}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agendamento.status)}`}>
                        {agendamento.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabela de agendamentos */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Tabela de Agendamentos</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordem</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fornecedor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mercadoria</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ocorrência</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="mt-2">Carregando...</p>
                    </td>
                  </tr>
                ) : agendamentos.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      Nenhum agendamento encontrado
                    </td>
                  </tr>
                ) : (
                  agendamentos.map((agendamento) => (
                    <tr key={agendamento.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agendamento.ordem_chegada ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex px-2 py-1 text-xs font-bold rounded bg-blue-100 text-blue-800">
                              #{agendamento.ordem_chegada}
                            </span>
                            {agendamento.hora_ordem_chegada && (
                              <span className="text-xs text-gray-500">{agendamento.hora_ordem_chegada}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{agendamento.fornecedor}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{agendamento.mercadorias}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agendamento.status)}`}>
                          {agendamento.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getOcorrenciaDisplay(agendamento.ocorrencia)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatarData(agendamento.data_programada)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Próximos agendamentos por dia */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Próximos Agendamentos por Dia</h2>
          </div>
          <div className="p-6">
            {agendamentosPorDia.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhum agendamento encontrado</p>
            ) : (
              <div className="space-y-4">
                {agendamentosPorDia.map((dia) => (
                  <div key={dia.data} className="border-l-4 border-primary-500 pl-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold text-gray-900">{dia.data}</h3>
                      <span className="bg-primary-100 text-primary-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        {dia.quantidade} agendamentos
                      </span>
                    </div>
                    <div className="space-y-2">
                      {dia.agendamentos.slice(0, 3).map((agendamento) => (
                        <div key={agendamento.id} className="bg-gray-50 p-3 rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{agendamento.codigo}</p>
                              <p className="text-sm text-gray-600">{agendamento.fornecedor}</p>
                              <p className="text-xs text-gray-500">{agendamento.mercadorias}</p>
                            </div>
                            <div className="text-right">
                              {agendamento.ordem_chegada && (
                                <span className="inline-flex px-2 py-1 text-xs font-bold rounded bg-blue-100 text-blue-800 mr-2">
                                  #{agendamento.ordem_chegada}
                                </span>
                              )}
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agendamento.status)}`}>
                                {agendamento.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {dia.agendamentos.length > 3 && (
                        <p className="text-sm text-gray-500 text-center">
                          +{dia.agendamentos.length - 3} mais agendamentos
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </>
        ) : (
          <DadosLogisticos filialAtual={filialAtual} />
        )}
      </div>
    </div>
  );
};

export default AreaRCA;
